import { useEffect, useMemo, useState, useRef } from "react";
import {
  api,
  type ControlPlaneSupervisorView,
  type MissionDetailResponse,
  type MissionEvidenceItemView,
  type MissionCheckpointView,
  type HumanGateView,
} from "../api";
import {
  CONTROL_PLANE_AREAS,
  CONTROL_PLANE_AREA_GROUPS,
  controlPlaneAreaFromHash,
  type ControlPlaneAreaId,
} from "./ownerNavigation";
import type { useDashboardData } from "./useDashboardData";
import { statusLabel } from "../domain/statusCopy";
import { ControlPlaneOverviewPanel } from "./ControlPlaneOverviewPanel";
import { QueuePanel } from "./QueuePanel";
import { AgentRoomPanel } from "./AgentRoomPanel";
import { HumanGateInbox } from "./HumanGateInbox";
import { SecretProvisionPanel } from "./SecretProvisionPanel";
import { ResourceGovernorPanel } from "./ResourceGovernorPanel";
import { ScopeAuthorityPanel } from "./ScopeAuthorityPanel";
import { FindingsLedgerPanel } from "./FindingsLedgerPanel";
import { MissionDetailDialog } from "./MissionDetailDialog";
import { PanelErrorBoundary } from "./PanelErrorBoundary";
import { NotificationInboxPanel } from "./CommandPanels";

/**
 * SIMO-OPERATOR-PLANE-TRUTH-NAV-INTAKE-CLOSURE-1 — Slice 5: die Owner-Control-
 * Plane-Fläche der Owner-Shell. KEIN visueller Rewrite: die bestehenden
 * CP-08..CP-15-Panels werden primär verdrahtet und konsolidiert (§22).
 *
 * Navigation: `#/control/<bereich>` — Deep-Links und Browser Back/Forward
 * laufen über hashchange (wie in der SimoT-Shell). Bereichsauswahl ist reine
 * Ansicht; jede Mutation bleibt in ihren bestehenden, geprüften Flächen.
 */

const AREA_LABELS: Record<ControlPlaneAreaId, string> = Object.fromEntries(
  CONTROL_PLANE_AREAS.map((a) => [a.id, a.label]),
) as Record<ControlPlaneAreaId, string>;

export function ControlPlaneSurface({ data }: { data: ReturnType<typeof useDashboardData> }): JSX.Element {
  const [area, setArea] = useState<ControlPlaneAreaId>(() => controlPlaneAreaFromHash(window.location.hash));
  const [missionDialogId, setMissionDialogId] = useState<string | null>(null);
  // IA-C3: EIN Aktualisieren für die ganze Fläche — lädt Dashboard-Daten UND
  // die selbstladenden Panels (Overview zuerst; Queue/Supervisor folgen in
  // ihren Slices) über den Refresh-Key nach.
  const [refreshKey, setRefreshKey] = useState(0);
  // OWNEXP F-09/F-10: Live-Takt + sichtbarer Datenstand — die Leitstelle ist
  // keine Momentaufnahme mehr; Aktualisieren gibt Feedback (aria-busy + Zeit).
  const [erfrischend, setErfrischend] = useState(false);
  // OWNEXP Red-Team M2: „Stand" ist die SERVER-generierte Zeit der Summary —
  // sie wandert nur bei tatsächlich eingetroffenen neuen Daten, nie beim
  // bloßen Takt-Trigger (Alt-Daten bleiben ehrlich als alt erkennbar).
  const serverStand = data.controlPlaneSummary?.generatedAt ?? null;

  useEffect(() => {
    const onHashChange = (): void => setArea(controlPlaneAreaFromHash(window.location.hash));
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  const refreshAll = (): void => {
    setErfrischend(true);
    data.refresh();
    setRefreshKey((key) => key + 1);
    window.setTimeout(() => setErfrischend(false), 600);
  };

  // Sanfter Live-Takt (30 s): Queue/Overview/Supervisor bleiben aktuell, ohne
  // Request-Sturm (F-11 bewusst NICHT durch Polling aller Shell-Quellen).
  useEffect(() => {
    const id = window.setInterval(() => {
      data.refresh();
      setRefreshKey((key) => key + 1);
    }, 30_000);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // OWNEXP Slice 9: Post-Submit-Notice (und andere Flächen) können ein
  // Mission-Detail per Ereignis anfordern — die Control Plane öffnet den
  // Dialog auf der Missions-&-Runs-Fläche (ein Wahrheitspfad, kein zweiter).
  useEffect(() => {
    const onOpenMission = (event: Event): void => {
      const missionId = (event as CustomEvent<string>).detail;
      if (typeof missionId === "string" && missionId) {
        setArea("missions-runs");
        setMissionDialogId(missionId);
      }
    };
    window.addEventListener("simo:open-mission", onOpenMission);
    return () => window.removeEventListener("simo:open-mission", onOpenMission);
  }, []);

  const openArea = (next: ControlPlaneAreaId): void => {
    window.location.hash = `#/control/${next}`;
    setArea(next);
  };

  const openMission = (missionId: string): void => setMissionDialogId(missionId);

  return (
    <section className="mx-auto flex min-h-full w-full max-w-[1600px] flex-col gap-4 p-4 sm:p-6" aria-label="Control Plane" data-testid="control-plane-surface">
      <div className="flex min-w-0 flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-jarvis-accent">Owner Control Plane</p>
          <h1 className="text-2xl font-semibold">Control Plane</h1>
          <p className="text-sm text-jarvis-muted">
            Operative Leitstellen-Wahrheit: Queue, Supervisor, Missions &amp; Runs, Gates, Resources, Scopes, Findings und Evidence — aus denselben Read-Models, keine konkurrierende Oberfläche.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[10px] text-jarvis-muted" data-testid="control-plane-stand">
            {serverStand
              ? `Datenstand (Server): ${new Date(serverStand).toLocaleTimeString("de-DE")} · Takt 30 s`
              : "erstes Laden…"}
          </span>
          <button
            className="btn px-3 py-1.5 text-xs"
            type="button"
            onClick={() => {
              window.dispatchEvent(new Event("simo:open-palette"));
            }}
            title="Suche & Befehle öffnen (Strg+K)"
          >
            Suche ⌘K
          </button>
          <button
            className="btn btn-primary px-3 py-1.5 text-xs"
            type="button"
            onClick={() => {
              window.location.hash = "#/home";
            }}
            title="Mission Center öffnen — Auftrag formulieren und einreichen"
          >
            Neue Mission
          </button>
          <button className="topbar-control" type="button" onClick={refreshAll} aria-busy={erfrischend} disabled={erfrischend}>
            {erfrischend ? "Aktualisiere…" : "Aktualisieren"}
          </button>
        </div>
      </div>

      {/* Hauptnavigation der Control Plane: nach Owner-Job gruppiert (DNA 03 §4,
          DC-23 — vier Gruppen statt 12 gleichrangiger Pills), flex-wrap statt Überdecken */}
      <nav className="-mx-1 flex flex-wrap items-start gap-x-5 gap-y-2 px-1 pb-1" aria-label="Control Plane Bereiche" data-testid="control-plane-area-nav">
        {CONTROL_PLANE_AREA_GROUPS.map((gruppe) => (
          <div key={gruppe.id} className="flex min-w-0 flex-col gap-1" data-testid={`control-plane-area-group-${gruppe.id}`}>
            <span className="px-3 text-[10px] font-semibold uppercase tracking-[0.14em] text-jarvis-muted/80">
              {gruppe.label}
            </span>
            <div className="flex flex-wrap gap-1">
              {gruppe.areas.map((areaId) => {
                const a = CONTROL_PLANE_AREAS.find((eintrag) => eintrag.id === areaId);
                if (!a) return null;
                return (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => openArea(a.id)}
                    aria-current={area === a.id ? "page" : undefined}
                    className={`shrink-0 rounded-lg border px-3 py-2 text-xs font-medium transition ${
                      area === a.id
                        ? "border-jarvis-accent/50 bg-jarvis-accent/15 text-jarvis-text"
                        : "border-white/10 bg-black/20 text-jarvis-muted hover:text-jarvis-text"
                    }`}
                  >
                    {a.label}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      <div className="min-h-0 flex-1">
        {/* UX-J Red-Team F1: Flächen-Container als Gate-Anker — Beweis-Marker
            werden flächen-scoped gemessen (Navigation/Topbar zählen nicht). */}
        <div data-testid={`cp-area-${area}`}>
        {/* OWNEXP Slice 12/15: Eine gestörte Fläche degradiert nur sich selbst
            (PanelErrorBoundary) — die Leitstelle bleibt bedienbar. */}
        <PanelErrorBoundary key={`overview-${area}`} label="Overview">
          {area === "overview" && <ControlPlaneOverviewPanel onOpenMission={openMission} onOpenArea={openArea} refreshKey={refreshKey} />}
        </PanelErrorBoundary>
        <PanelErrorBoundary key={`queue-${area}`} label="Queue">
          {area === "queue" && <QueuePanel onOpenMission={openMission} refreshKey={refreshKey} />}
        </PanelErrorBoundary>
        <PanelErrorBoundary key={`mr-${area}`} label="Missions & Runs">
          {area === "missions-runs" && <MissionsRunsPanel data={data} onOpenMission={openMission} />}
        </PanelErrorBoundary>
        <PanelErrorBoundary key={`sup-${area}`} label="Supervisor">
          {area === "supervisor" && <SupervisorPanel />}
        </PanelErrorBoundary>
        <PanelErrorBoundary key={`ag-${area}`} label="Agents">
          {area === "agents" && <AgentsReadbackPanel data={data} />}
        </PanelErrorBoundary>
        <PanelErrorBoundary key={`ar-${area}`} label="Agent Room">
          {area === "agent-room" && <AgentRoomPanel />}
        </PanelErrorBoundary>
        <PanelErrorBoundary key={`hg-${area}`} label="Human Gates">
          {area === "gates" && (
            <>
              <HumanGateInbox />
              {/* SECRET-LANE (POST-GATE-INCIDENT §4): reale, sichere Eingabeoberflaeche — nur bei offenen Requests sichtbar. */}
              <SecretProvisionPanel />
            </>
          )}
        </PanelErrorBoundary>
        <PanelErrorBoundary key={`res-${area}`} label="Resources">
          {area === "resources" && <ResourceGovernorPanel />}
        </PanelErrorBoundary>
        <PanelErrorBoundary key={`sc-${area}`} label="Scopes & Authority">
          {area === "scopes" && <ScopeAuthorityPanel />}
        </PanelErrorBoundary>
        <PanelErrorBoundary key={`fi-${area}`} label="Findings">
          {area === "findings" && <FindingsLedgerPanel />}
        </PanelErrorBoundary>
        <PanelErrorBoundary key={`ev-${area}`} label="Evidence & Reports">
          {area === "evidence" && <EvidenceReportsPanel onOpenMission={openMission} data={data} />}
        </PanelErrorBoundary>
        <PanelErrorBoundary key={`at-${area}`} label="Attention">
          {area === "attention" && (
            <>
              <AttentionCenterPanel data={data} />
              {/* OWNEXP F-05: Hinweise haben hier ihre Liste — der Topbar-Chip
                  führt hierher, kein toter Zähler. */}
              <NotificationInboxPanel data={data} />
            </>
          )}
        </PanelErrorBoundary>
        </div>
      </div>

      {missionDialogId && <MissionDetailDialog missionId={missionDialogId} onClose={() => setMissionDialogId(null)} />}
    </section>
  );
}

// ── Kanonische Zahlen: nie Fake-0 — fehlendes Read-Model heißt UNAVAILABLE ──

function KanonischerZaehler({ label, wert, hinweis }: { label: string; wert: number | null; hinweis?: string }): JSX.Element {
  return (
    <div className="card p-4" data-testid={`canonical-counter-${label.replace(/[^a-z0-9-]/gi, "-").toLowerCase()}`}>
      <span className="text-xs text-jarvis-muted">{label}</span>
      <strong className="block text-2xl">{wert === null ? <span className="text-amber-300">UNAVAILABLE</span> : wert}</strong>
      {hinweis && <p className="mt-1 text-[10px] leading-snug text-jarvis-muted">{hinweis}</p>}
    </div>
  );
}

/** Slice 3 — Missions & Runs in kanonischer Semantik mit One-Click-Drilldown. */
function MissionsRunsPanel({ data, onOpenMission }: { data: ReturnType<typeof useDashboardData>; onOpenMission: (missionId: string) => void }): JSX.Element {
  const semantics = data.controlPlaneSummary?.semantics ?? null;
  const runs = semantics?.runs.activeList ?? null;
  const missions = semantics?.missions.activeList ?? null;
  return (
    <div className="flex flex-col gap-4" data-testid="missions-runs-panel">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <KanonischerZaehler label="Aktive Missionen" wert={semantics ? semantics.missions.active : null} hinweis="Kanonisch: missions mit Status active" />
        <KanonischerZaehler label="Aktive Runs" wert={semantics ? semantics.runs.nonTerminal : null} hinweis="Kanonisch: control_plane_runs non-terminal" />
        <KanonischerZaehler label="Aktive Leases" wert={semantics ? semantics.leases.active : null} hinweis="work_item_leases aktiv (Writer-Fencing)" />
        <KanonischerZaehler label="Chat-/Tool-Runs" wert={null} hinweis="Zählen bewusst NICHT als Runs (Semantik-Vertrag)" />
      </div>
      {data.controlPlaneSummaryError && (
        <div className="card border border-amber-300/30 p-3 text-xs text-amber-200" role="status">
          Control-Plane-Summary nicht verfügbar: {data.controlPlaneSummaryError.message} — Kanonische Zähler bleiben UNAVAILABLE statt 0.
        </div>
      )}

      <div className="card p-4">
        <h2 className="text-sm font-semibold">Aktive Control-Plane-Runs</h2>
        <p className="mt-1 text-xs text-jarvis-muted">Mission-Runs (nicht Chat-/Tool-Runs). Klick auf eine Zeile öffnet die Mission.</p>
        <div className="mt-3 grid gap-2">
          {runs === null ? (
            <p className="text-xs text-amber-300">RUN READ MODEL UNAVAILABLE — keine Ersatzwerte aus Chat-Runs.</p>
          ) : runs.length === 0 ? (
            semantics && semantics.leases.active > 0 ? (
              <div className="rounded-lg border border-white/10 bg-black/20 p-4 text-xs leading-relaxed text-jarvis-muted">
                <strong className="block text-sm text-jarvis-text">Ein Writer arbeitet — Run-Projektion folgt.</strong>
                {semantics.leases.active} aktive Writer-Lease(s) belegen laufende Arbeit (Queue-Zeile „Läuft gerade");
                die Run-Zeile erscheint, sobald die Run-Projektion den Start registriert hat — dieselbe Quelle, kein Widerspruch.
              </div>
            ) : (
              <div className="rounded-lg border border-white/10 bg-black/20 p-4 text-xs leading-relaxed text-jarvis-muted">
                <strong className="block text-sm text-jarvis-text">Kein Run aktiv — ehrlicher Nullzustand.</strong>
                Die Queue entscheidet, was als Nächstes läuft: nächstes Item, Wartegrund und nächste Bedingung stehen im Bereich Supervisor.
              </div>
            )
          ) : runs.map((run) => {
            const runId = String(run.run_id ?? "");
            const missionId = run.mission_id == null ? null : String(run.mission_id);
            const status = String(run.status ?? "");
            const quelle = `${String(run.source_type ?? "")}/${String(run.source_id ?? "")}`;
            // OWNEXP Slice 4 (AP-01): menschenlesbarer Titel primär, Kennung sekundar
            const titel = String(run.title ?? run.goal ?? "").trim() || runId;
            return (
              <button
                key={runId}
                type="button"
                onClick={() => missionId && onOpenMission(missionId)}
                disabled={!missionId}
                className="rounded-lg border border-white/10 bg-black/20 p-3 text-left transition hover:border-jarvis-accent/40 disabled:cursor-default"
                data-testid={`cprun-${runId}`}
              >
                <div className="flex min-w-0 flex-wrap items-center justify-between gap-2">
                  <strong className="min-w-0 truncate text-sm" title={runId}>{titel}</strong>
                  <span className="rounded-md border border-jarvis-accent/25 px-2 py-0.5 text-[10px] tracking-wider text-jarvis-accent" title={status || undefined}>
                    {statusLabel(status) || (status ? status : "Status nicht gemessen")}
                  </span>
                </div>
                <p className="mt-1 text-[11px] text-jarvis-muted">
                  <span className="font-mono">{runId}</span> · Quelle {quelle}
                  {missionId ? ` · Mission ${missionId}` : " · ohne Mission-Bindung"}
                  {` · Statuswechsel ${String(run.status_changed_at ?? "—")}`}
                </p>
              </button>
            );
          })}
        </div>
      </div>

      <div className="card p-4">
        <h2 className="text-sm font-semibold">Aktive Missionen</h2>
        <div className="mt-3 grid gap-2">
          {missions === null ? (
            <p className="text-xs text-amber-300">MISSION READ MODEL UNAVAILABLE.</p>
          ) : missions.length === 0 ? (
            <p className="text-xs text-jarvis-muted">Keine aktive Mission. Neue Missionen laufen über den kanonischen Intake (Mission Center).</p>
          ) : missions.map((m) => (
            <button
              key={String(m.id)}
              type="button"
              onClick={() => onOpenMission(String(m.id))}
              className="rounded-lg border border-white/10 bg-black/20 p-3 text-left transition hover:border-jarvis-accent/40"
              data-testid={`cpmission-${String(m.id)}`}
            >
              <div className="flex min-w-0 flex-wrap items-center justify-between gap-2">
                <strong className="min-w-0 truncate text-sm">{String((m as { title?: string }).title ?? String(m.goal ?? m.id).slice(0, 120))}</strong>
                <span className="font-mono text-[10px] text-jarvis-muted">{String(m.status ?? "—")}</span>
              </div>
              <p className="mt-1 font-mono text-[10px] text-jarvis-muted">{String(m.id)} · aktualisiert {String(m.updated_at ?? "—")}</p>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Slice 6 — Supervisor-Seite: Health, Tick, Admission, next eligible + WARUM. */
function SupervisorPanel(): JSX.Element {
  const [supervisor, setSupervisor] = useState<ControlPlaneSupervisorView | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let abgebaut = false;
    const load = (): void => {
      api.controlPlaneSupervisor()
        .then((r) => { if (!abgebaut) { setSupervisor(r); setError(null); } })
        .catch((e) => { if (!abgebaut) setError(e instanceof Error ? e.message : String(e)); });
    };
    load();
    const id = window.setInterval(load, 5000);
    return () => { abgebaut = true; window.clearInterval(id); };
  }, []);

  if (error) {
    return (
      <div className="card border border-amber-300/30 p-4" role="status" data-testid="supervisor-unavailable">
        <h2 className="text-sm font-semibold text-amber-200">Supervisor-Read-Model UNAVAILABLE</h2>
        <p className="mt-1 text-xs text-jarvis-muted">{error}</p>
        <p className="mt-1 text-xs text-jarvis-muted">Keine Ersatzwerte — der operative Zustand bleibt unbekannt, bis der Endpunkt wieder antwortet.</p>
      </div>
    );
  }
  if (!supervisor) {
    return <div className="card p-4 text-sm text-jarvis-muted" data-testid="supervisor-loading">Supervisor-Read-Model wird geladen…</div>;
  }

  const gesund = supervisor.health.schedulerRunning && supervisor.health.dbOk;
  // OWNEXP Slice 7 (R-SUP-1, Mission §8): die Entscheidung des letzten Taks
  // als Owner-Satz primär — „SIMO hat … nicht gestartet, weil …".
  const letzteEntscheidung: string = supervisor.lastTick
    ? supervisor.lastTick.admitted > 0
      ? `SIMO hat im letzten Takt ${supervisor.lastTick.admitted} Item(s) gestartet.`
      : supervisor.nextEligible[0]
        ? `SIMO hat im letzten Takt nichts gestartet — nächster Kandidat wartet noch: ${supervisor.nextEligible[0].warumWartend}.`
        : "SIMO hat im letzten Takt nichts gestartet — kein Item war startbereit (alle erledigt, geparkt oder blockiert)."
    : "Noch kein Takt protokolliert — Aussage kommt mit dem ersten Supervisor-Takt.";
  return (
    <div className="flex flex-col gap-4" data-testid="supervisor-panel">
      {/* Entscheidung primär (DC-21): was hat SIMO zuletzt entschieden? */}
      <div className="card p-4" data-testid="supervisor-entscheidung">
        <span className="text-[10px] uppercase tracking-[0.14em] text-jarvis-muted">Letzte Supervisor-Entscheidung</span>
        <strong className={`mt-1 block text-lg ${gesund ? "" : "text-amber-300"}`}>{letzteEntscheidung}</strong>
        <p className="mt-1 text-xs text-jarvis-muted">
          {gesund ? "Supervisor aktiv" : "Supervisor eingeschränkt"} · nächster Takt alle {Math.round(supervisor.cadence.intervalMs / 1000)} s
        </p>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <div className={`card p-4 ${gesund ? "" : "border-amber-300/40"}`}>
          <span className="text-xs text-jarvis-muted">Zustand</span>
          <strong className="block text-xl">{gesund ? "Aktiv" : "Eingeschränkt"}</strong>
          <p className="mt-1 text-[10px] leading-snug break-words text-jarvis-muted/90">
            Scheduler {supervisor.health.schedulerRunning ? "läuft" : "gestoppt"} · Executor {supervisor.health.executorRunning ? "läuft" : supervisor.health.executorConfigured ? "konfiguriert, gestoppt" : "nicht konfiguriert"} · DB {supervisor.health.dbOk ? "ok" : "Fehler"}
          </p>
        </div>
        <div className="card p-4">
          <span className="text-xs text-jarvis-muted">Letzter Takt</span>
          <strong className="block text-xl">{supervisor.lastTick ? new Date(supervisor.lastTick.finishedAt).toLocaleTimeString("de-DE") : "—"}</strong>
          <p className="mt-1 text-[10px] leading-snug break-words text-jarvis-muted/90">
            {supervisor.lastTick
              ? `${supervisor.lastTick.durationMs} ms · ${supervisor.lastTick.transitions} Zustandswechsel`
              : "Seit Prozessstart noch kein Tick protokolliert."}
          </p>
        </div>
        <div className="card p-4">
          <span className="text-xs text-jarvis-muted">Takt-Rhythmus</span>
          <strong className="block text-xl">{Math.round(supervisor.cadence.intervalMs / 1000)} s</strong>
          <p className="mt-1 text-[10px] leading-snug break-words text-jarvis-muted/90">Technische Invariante: LLM-Aufrufe pro Takt = {supervisor.cadence.llmCallsPerTick} (Supervisor entscheidet deterministisch, ohne Modell)</p>
        </div>
        <div className="card p-4">
          <span className="text-xs text-jarvis-muted">Letzter Start</span>
          <strong className="block text-xs">{supervisor.lastAdmission ? new Date(supervisor.lastAdmission.occurredAt).toLocaleString("de-DE") : "—"}</strong>
          <p className="mt-1 text-[10px] leading-snug break-words text-jarvis-muted/90">{supervisor.lastAdmission ? `${supervisor.lastAdmission.title ?? supervisor.lastAdmission.workitemId ?? supervisor.lastAdmission.runId}` : "Noch kein Start in der Event-Historie."}</p>
        </div>
      </div>

      <div className="card p-4">
        <h2 className="text-sm font-semibold">Next Eligible — was läuft als Nächstes, und warum wartet es?</h2>
        <div className="mt-3 grid gap-2">
          {supervisor.nextEligible.length === 0 ? (
            <p className="text-xs text-jarvis-muted">Kein Admission-Kandidat (weder READY noch wartend) im Read-Model.</p>
          ) : supervisor.nextEligible.map((k) => (
            <article key={k.workitemId} className="rounded-lg border border-white/10 bg-black/20 p-3" data-testid={`next-eligible-${k.workitemId}`}>
              <div className="flex min-w-0 flex-wrap items-center justify-between gap-2">
                <strong className="min-w-0 truncate text-sm">{k.title}</strong>
                <span className="rounded-md border border-jarvis-accent/25 px-2 py-0.5 text-[10px] tracking-wider text-jarvis-accent" title={`${k.queueState ?? "—"} · ${k.priority}`}>
                  {statusLabel(k.queueState) ?? "Zustand unbekannt"} · {k.priority === "urgent" ? "Dringend" : k.priority === "high" ? "Hoch" : k.priority === "low" ? "Niedrig" : "Normal"}
                </span>
              </div>
              <p className="mt-1 text-[11px] text-amber-200/90">Warum wartend: {k.warumWartend}</p>
              <p className="mt-0.5 text-[11px] text-jarvis-muted">Nächste Bedingung: {k.naechsteBedingung}</p>
              {k.wartetAuf.length > 0 && <p className="mt-0.5 font-mono text-[10px] text-jarvis-muted">wartet auf: {k.wartetAuf.join(", ")}</p>}
              <p className="mt-0.5 font-mono text-[10px] text-jarvis-muted">{k.workitemId}{k.missionId ? ` · Mission ${k.missionId}` : ""}</p>
            </article>
          ))}
        </div>
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <div className="card p-4">
          <h2 className="text-sm font-semibold">Letzte Transitionen</h2>
          <div className="simo-thin-scroll mt-3 max-h-72 overflow-y-auto">
            {supervisor.recentTransitions.length === 0 ? (
              <p className="text-xs text-jarvis-muted">Keine Queue-Transitionen in der Event-Historie (still ist ein Zustand, kein Fehler).</p>
            ) : (
              <ul className="grid gap-1.5">
                {supervisor.recentTransitions.map((t) => (
                  <li key={t.eventId} className="rounded-md border border-white/10 bg-black/20 p-2 text-[11px]">
                    <span className="text-jarvis-text">{t.title ?? t.workitemId ?? t.runId}</span>
                    <span className="text-jarvis-muted"> · {new Date(t.occurredAt).toLocaleString("de-DE")}</span>
                    <span className="block truncate font-mono text-[10px] text-jarvis-accent/80">{t.eventType}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <div className="card p-4">
          <h2 className="text-sm font-semibold">Letzte Fehler (Lauf-Ebene, run.failed)</h2>
          <p className="mt-0.5 text-[10px] text-jarvis-muted">Fehlgeschlagene Queue-Items zählt der Bereich Attention (Item-Ebene) — beide Ebenen sind bewusst getrennt ausgewiesen.</p>
          <div className="simo-thin-scroll mt-3 max-h-72 overflow-y-auto">
            {supervisor.recentErrors.length === 0 ? (
              <p className="text-xs text-jarvis-muted">Keine Fehler-Signale (Tick-Fehler oder run.failed) in der recent-Historie.</p>
            ) : (
              <ul className="grid gap-1.5">
                {supervisor.recentErrors.map((f, i) => (
                  <li key={`${f.kind}-${f.at}-${i}`} className="rounded-md border border-red-300/20 bg-red-300/5 p-2 text-[11px] text-red-200/90">
                    <span className="font-mono">{f.kind}</span> · {new Date(f.at).toLocaleString("de-DE")}
                    <span className="block truncate text-jarvis-muted">{f.detail}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Slice 5 — Agents-Bereich: Registry-Readback ohne Fake-Zahlen. */
function AgentsReadbackPanel({ data }: { data: ReturnType<typeof useDashboardData> }): JSX.Element {
  const studio = data.agentOsStudio;
  const registry = data.agentRegistry;
  const summary = studio?.summary ?? null;
  const gebunden = studio?.registryRevision.status === "bound";
  return (
    <div className="flex flex-col gap-4" data-testid="agents-panel">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <KanonischerZaehler label="Agenten" wert={summary && gebunden ? summary.agents : null} hinweis={gebunden ? "Registry gebunden" : `Registry ${studio?.registryRevision.status ?? "unavailable"} (${studio?.registryRevision.bindingReason ?? data.agentOsStudioError?.message ?? "kein Read-Model"})`} />
        <KanonischerZaehler label="Skills" wert={summary && gebunden ? summary.skills : null} />
        <KanonischerZaehler label="Routinen" wert={summary && gebunden ? summary.routines : null} />
        <KanonischerZaehler label="Aktive Runs (Studio)" wert={summary && gebunden ? summary.activeRuns : null} hinweis="Studio-Read-Model; kanonische Runs siehe Missions & Runs" />
      </div>
      <div className="card p-4">
        <h2 className="text-sm font-semibold">Live-Agent-Readbacks</h2>
        <p className="mt-1 text-xs text-jarvis-muted">{data.agents.length} Agent-Readbacks aus dem Command-Center (Chat-/Agent-Graph-Sicht).</p>
        <div className="mt-3 grid gap-2 md:grid-cols-2 xl:grid-cols-3">
          {data.agents.length === 0 ? (
            <p className="text-xs text-jarvis-muted">Kein Agent-Readback — kein Agent behauptet Arbeit ohne Signal.</p>
          ) : data.agents.map((agent) => (
            <div key={agent.id} className="rounded-lg border border-white/10 bg-black/20 p-3">
              <div className="flex items-center justify-between gap-2">
                <strong className="min-w-0 truncate text-sm">{agent.name}</strong>
                <span className="shrink-0 rounded-md border border-current/20 px-1.5 py-0.5 text-[9px] uppercase tracking-wider opacity-80">{agent.liveStatus}</span>
              </div>
              <p className="mt-1 text-[11px] text-jarvis-muted">{agent.activeRunIds?.length ?? 0} aktive Run-Referenzen · Risiko {agent.highestRiskLevel ?? "nicht gemeldet"}</p>
            </div>
          ))}
        </div>
        {registry && (
          <p className="mt-3 text-[10px] text-jarvis-muted">
            Registry: {registry.agents.length} Registereinträge · Read-Model {gebunden ? "gebunden" : "DEGRADED"}{studio?.registryRevision.revision ? ` · Revision ${studio.registryRevision.revision.slice(0, 8)}` : ""}
          </p>
        )}
      </div>
    </div>
  );
}

// ── Slice 7 — Evidence & Reports: strukturiert, keine LLM-Berichtswände ──

interface Missionsbericht {
  missionId: string;
  detail: MissionDetailResponse | null;
  checkpoints: MissionCheckpointView[];
  evidence: MissionEvidenceItemView[];
  gatesOffen: HumanGateView[];
  fehler: string | null;
}

function EvidenceReportsPanel({
  onOpenMission,
  data,
}: {
  onOpenMission: (missionId: string) => void;
  data: ReturnType<typeof useDashboardData>;
}): JSX.Element {
  // OWNEXP F-02-Fix (Claude): nie wieder Endlos-Laden — die kanonische
  // Missionsliste kommt sofort aus dem Summary-Read-Model (bereits geladen);
  // /api/missions ist nur noch Anreicherung MIT Timeout (Proxy-Abbruch macht
  // die Fläche nicht mehr blind).
  const summaryMissions = (data.controlPlaneSummary?.semantics?.missions?.activeList ?? null) as Array<{ id: string; goal: string; status: string; updated_at?: string }> | null;
  // OWNEXP Red-Team M1: Summary-Liste per Ref aktuell halten — der Fehler-
  // Fallback nutzt die NEUESTE kanonische Liste, nicht den Mount-Snapshot.
  const summaryMissionsRef = useRef(summaryMissions);
  summaryMissionsRef.current = summaryMissions;
  const [missions, setMissions] = useState<Array<{ id: string; goal: string; status: string; updated_at?: string }> | null>(summaryMissions);
  const [missionsError, setMissionsError] = useState<string | null>(null);
  const [gewaehlt, setGewaehlt] = useState<string | null>(null);
  const [bericht, setBericht] = useState<Missionsbericht | null>(null);

  useEffect(() => {
    const abbruch = new AbortController();
    const zeitnehmer = window.setTimeout(() => abbruch.abort(), 8000);
    api.missions({ signal: abbruch.signal })
      .then((r) => setMissions(r.missions.slice(0, 60)))
      .catch((e) => {
        // Kanonische Fallback-Liste (aktuellste Summary) bleibt stehen; nur
        // Hinweis, kein Blockieren, kein leeres „Keine Mission"-Falsch-Label.
        setMissions((vorher) => (vorher && vorher.length > 0 ? vorher : summaryMissionsRef.current ?? []));
        setMissionsError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => window.clearTimeout(zeitnehmer));
    return () => {
      abbruch.abort();
      window.clearTimeout(zeitnehmer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!gewaehlt) return;
    let abgebaut = false;
    setBericht(null);
    Promise.all([
      api.missionDetail(gewaehlt),
      api.missionCheckpoints(gewaehlt, 100).catch(() => null),
      api.missionEvidence(gewaehlt, 100).catch(() => null),
      // OPNAV1 Truth-Fixture: Owner-Aktion nur aus DER einen Gate-Authority
      // (frische Bundle), nie aus abgelaufenen OPEN-Gate-Zeilen.
      api.gateBundles().catch(() => null),
    ]).then(([d, c, e, g]) => {
      if (abgebaut) return;
      setBericht({
        missionId: gewaehlt,
        detail: d,
        checkpoints: c?.checkpoints ?? [],
        evidence: e?.evidence ?? [],
        gatesOffen: (g?.bundles ?? []).filter((bundle) => bundle.missionId === gewaehlt).flatMap((bundle) => bundle.gates as unknown as HumanGateView[]),
        fehler: null,
      });
    }).catch((err) => {
      if (!abgebaut) setBericht({ missionId: gewaehlt, detail: null, checkpoints: [], evidence: [], gatesOffen: [], fehler: err instanceof Error ? err.message : String(err) });
    });
    return () => { abgebaut = true; };
  }, [gewaehlt]);

  const sortierteMissions = useMemo(() => {
    if (!missions) return null;
    return [...missions].sort((a, b) => (b.updated_at ?? "").localeCompare(a.updated_at ?? ""));
  }, [missions]);

  return (
    <div className="flex flex-col gap-4" data-testid="evidence-reports-panel">
      <div className="card p-4">
        <h2 className="text-sm font-semibold">Reports &amp; Evidence je Mission</h2>
        <p className="mt-1 text-xs text-jarvis-muted">Strukturierter Bericht aus Live-Daten (Mission, Queue-Items, Checkpoints, Gates, Evidence) — ohne LLM-Berichtswände.</p>
        {missionsError && (
          <p className="mt-2 text-xs text-amber-300" role="status">
            Vollständige Missionsliste zeitweise nicht abrufbar ({missionsError}) — es werden die aktiven
            Missionen aus der kanonischen Zusammenfassung angezeigt.
          </p>
        )}
        <div className="simo-thin-scroll mt-3 flex max-h-48 flex-col gap-1 overflow-y-auto">
          {sortierteMissions === null ? (
            <p className="text-xs text-jarvis-muted">Missionen werden geladen…</p>
          ) : sortierteMissions.length === 0 ? (
            <p className="text-xs text-jarvis-muted">Keine Mission im Read-Model.</p>
          ) : sortierteMissions.map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => setGewaehlt(m.id)}
              className={`rounded-lg border p-2 text-left text-xs transition ${gewaehlt === m.id ? "border-jarvis-accent/50 bg-jarvis-accent/10" : "border-white/10 bg-black/20 hover:border-jarvis-accent/30"}`}
            >
              <span className="block truncate font-medium">{m.goal.slice(0, 100)}</span>
              <span className="mt-0.5 block font-mono text-[10px] text-jarvis-muted">{m.id} · {m.status} · {m.updated_at ? new Date(m.updated_at).toLocaleString("de-DE") : "—"}</span>
            </button>
          ))}
        </div>
      </div>

      {bericht && (
        <div className="card p-4" data-testid="mission-report">
          {bericht.fehler && <p className="mb-3 text-xs text-amber-300">Bericht unvollständig: {bericht.fehler}</p>}
          {bericht.detail ? (
            <>
              <div className="flex min-w-0 flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="text-sm font-semibold">Live-Status</h3>
                  <p className="mt-1 text-xs text-jarvis-muted">{String(bericht.detail.mission.goal ?? "")}</p>
                  <p className="mt-1 font-mono text-[10px] text-jarvis-muted">{bericht.missionId} · Status {String(bericht.detail.mission.status ?? "—")} · Authority {String(bericht.detail.mission.execution_authority ?? "—")}</p>
                </div>
                <button type="button" className="topbar-control min-h-9 text-xs" onClick={() => onOpenMission(bericht.missionId)}>Vollständige Missionsdetail-Ansicht öffnen</button>
              </div>

              <div className="mt-4 grid gap-3 xl:grid-cols-2">
                <section>
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-jarvis-muted">Milestones (Checkpoints)</h4>
                  <div className="simo-thin-scroll mt-2 max-h-56 overflow-y-auto">
                    {bericht.checkpoints.length === 0 ? (
                      <p className="text-[11px] text-jarvis-muted">Kein Checkpoint aufgezeichnet (noch kein Milestone erreicht — kein Fehler).</p>
                    ) : bericht.checkpoints.slice().reverse().map((cp) => (
                      <div key={String(cp.checkpoint_id)} className="mb-1 rounded-md border border-white/10 bg-black/20 p-2 text-[11px]">
                        <span className="font-mono text-jarvis-accent/90">#{String(cp.seq)} {String(cp.current_block ?? "")}</span>
                        <span className="text-jarvis-muted"> · {String(cp.mission_state ?? "")} · {new Date(String(cp.created_at)).toLocaleString("de-DE")}</span>
                      </div>
                    ))}
                  </div>
                </section>

                <section>
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-jarvis-muted">Wait / Blocker / Owner-Aktion</h4>
                  <div className="mt-2 grid gap-1.5">
                    {bericht.detail.items.filter((item) => item.queue_state && !["COMPLETED", "FAILED"].includes(item.queue_state)).length === 0 && bericht.gatesOffen.length === 0 ? (
                      <p className="text-[11px] text-jarvis-muted">Kein offener Wait-/Blocker-Zustand.</p>
                    ) : bericht.detail.items.filter((item) => item.queue_state && !["COMPLETED", "FAILED"].includes(item.queue_state)).map((item) => (
                      <div key={item.id} className="rounded-md border border-amber-300/25 bg-amber-300/5 p-2 text-[11px]">
                        {/* UX-J Red-Team F3: Owner-Label primär (amber), Rohcode mono sekundar */}
                        <span className="font-medium text-amber-200">{statusLabel(item.queue_state) ?? "Zustand unbekannt"}</span>
                        <span className="font-mono text-[10px] text-jarvis-muted"> · {item.queue_state}</span>
                        <span className="block text-jarvis-muted">{item.title}</span>
                        {item.queue_state_reason ? <span className="block text-jarvis-muted">Grund: {item.queue_state_reason}</span> : null}
                        {item.queue_state === "WAIT_OWNER" || item.queue_state === "WAIT_HUMAN" ? <span className="block text-amber-200">Owner-Aktion: Entscheidung im Human-Gate-Inbox treffen.</span> : null}
                      </div>
                    ))}
                    {bericht.gatesOffen.map((gate) => (
                      <div key={gate.gateId} className="rounded-md border border-amber-300/25 bg-amber-300/5 p-2 text-[11px]">
                        <span className="font-mono text-amber-200">GATE {gate.actorClass}</span>
                        <span className="text-jarvis-muted"> · {gate.whyRequired} — {gate.exactAction}</span>
                      </div>
                    ))}
                  </div>

                  <h4 className="mt-3 text-xs font-semibold uppercase tracking-wider text-jarvis-muted">Failure / Incident</h4>
                  <div className="mt-2 grid gap-1.5">
                    {bericht.detail.items.filter((item) => item.queue_state === "FAILED" || item.status === "blocked").length === 0 ? (
                      <p className="text-[11px] text-jarvis-muted">Kein FAILED-/Incident-Zustand im Item-Bestand.</p>
                    ) : bericht.detail.items.filter((item) => item.queue_state === "FAILED" || item.status === "blocked").map((item) => (
                      <div key={item.id} className="rounded-md border border-red-300/25 bg-red-300/5 p-2 text-[11px] text-red-200/90">
                        {item.title} · {item.queue_state ?? item.status}{item.queue_state_reason ? ` · ${item.queue_state_reason}` : ""}
                      </div>
                    ))}
                  </div>
                </section>
              </div>

              <section className="mt-4">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-jarvis-muted">Evidence ({bericht.evidence.length})</h4>
                <div className="simo-thin-scroll mt-2 max-h-56 overflow-y-auto">
                  {bericht.evidence.length === 0 ? (
                    <p className="text-[11px] text-jarvis-muted">Noch keine Evidence für diese Mission.</p>
                  ) : bericht.evidence.slice().reverse().map((ev) => (
                    <div key={String(ev.evidenceId)} className="mb-1 rounded-md border border-white/10 bg-black/20 p-2 text-[11px]">
                      <span className="font-mono text-jarvis-accent/90">{ev.evidenceKind}</span>
                      {/* CP-16: Verifikationsstand des Beleg-Inhalts (kein bloßer Dateipfad-Verweis) */}
                      <span className={String(ev.contentBinding ?? "") === "VERIFIED_CONTENT" ? "text-emerald-400" : "text-amber-400/90"}> · {String(ev.contentBinding ?? "UNKNOWN")}</span>
                      <span className="text-jarvis-muted"> · {ev.subjectType}/{String(ev.subjectId).slice(0, 40)} · {new Date(ev.createdAt).toLocaleString("de-DE")}</span>
                      {ev.sha256 ? <span className="block truncate font-mono text-[10px] text-jarvis-muted">sha256 {ev.sha256.slice(0, 16)}…</span> : null}
                      {ev.artifactRef ? <span className="block truncate font-mono text-[10px] text-jarvis-muted">{ev.artifactRef}</span> : null}
                    </div>
                  ))}
                </div>
              </section>
            </>
          ) : !bericht.fehler ? (
            <p className="text-xs text-jarvis-muted">Bericht wird geladen…</p>
          ) : null}
        </div>
      )}
    </div>
  );
}

/** Slice 7 — Attention Center: gebündelte operative Aufmerksamkeit. */
function AttentionCenterPanel({ data }: { data: ReturnType<typeof useDashboardData> }): JSX.Element {
  const summary = data.controlPlaneSummary;
  const semantics = summary?.semantics ?? null;
  const queueStates = summary?.queue.states ?? [];
  const failedStates = queueStates.filter((s) => s.state === "FAILED");
  const blockedStates = queueStates.filter((s) => s.state === "BLOCKED");
  const waitExternal = queueStates.filter((s) => s.state === "WAIT_EXTERNAL" || s.state === "WAIT_OWNER" || s.state === "WAIT_RESOURCE" || s.state === "WAIT_HUMAN");
  const completedStates = queueStates.filter((s) => s.state === "COMPLETED");
  const kritisch = (summary?.findings.open ?? 0) > 0;
  const karte = (label: string, wert: number | null, ton: "warn" | "danger" | "info" | "ok", detail: string): JSX.Element => (
    <div
      className={`card p-4 ${ton === "danger" ? "border-red-300/30" : ton === "warn" ? "border-amber-300/30" : ""}`}
      data-testid={`attention-${label.replace(/[^a-z0-9-]/gi, "-").toLowerCase()}`}
    >
      <p className="text-[10px] uppercase tracking-[0.18em] text-jarvis-muted">{label}</p>
      <strong className="mt-1 block text-2xl">{wert === null ? <span className="text-amber-300">UNAVAILABLE</span> : wert}</strong>
      <p className="mt-1 text-[10px] leading-snug text-jarvis-muted">{detail}</p>
    </div>
  );
  return (
    <div className="flex flex-col gap-4" data-testid="attention-panel">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6">
        {karte("ACTION REQUIRED", summary ? summary.gates.open : null, "warn", "Offene Owner-Entscheidungen (Human Gates).")}
        {karte("CRITICAL", summary ? summary.findings.open : null, kritisch ? "danger" : "ok", "Offene Findings (Severity im Findings-Bereich).")}
        {karte("BLOCKED", blockedStates[0]?.count ?? (summary ? 0 : null), "danger", "Items mit unerfüllten Dependencies oder Blockern.")}
        {karte("FAILED", failedStates[0]?.count ?? (summary ? 0 : null), "danger", "Fehlgeschlagene Queue-Items (Retry-/Recovery-Pfad).")}
        {karte("WAIT_EXTERNAL", waitExternal.reduce((s, e) => s + e.count, 0) ?? (summary ? 0 : null), "info", "Wartet auf Owner/Ressource/externes Ereignis — Gründe im Queue-Bereich.")}
        {karte("RECENT COMPLETION", completedStates[0]?.count ?? (summary ? 0 : null), "ok", "Abgeschlossene Items (Queue-State COMPLETED).")}
      </div>
      {data.controlPlaneSummaryError && (
        <div className="card border border-amber-300/30 p-3 text-xs text-amber-200" role="status">
          Attention-Quelle (Control-Plane-Summary) nicht verfügbar: {data.controlPlaneSummaryError.message} — Kacheln zeigen UNAVAILABLE, keine Fake-0.
        </div>
      )}
      <div className="card p-4">
        <h2 className="text-sm font-semibold">Nächste automatische Aktion</h2>
        <p className="mt-1 text-xs text-jarvis-muted">
          Der Supervisor entscheidet im Takt ({Math.round((summary?.system.supervisor.tickIntervalMsDefault ?? 5000) / 1000)} s Standard, LLM-frei).
          {semantics && semantics.runs.nonTerminal > 0
            ? ` Aktuell ${semantics.runs.nonTerminal} Run(s) aktiv — Details im Bereich Missions & Runs.`
            : " Was als Nächstes läuft und warum es ggf. wartet, steht im Bereich Supervisor (NEXT ELIGIBLE / WHY WAITING / NEXT CONDITION)."}
        </p>
      </div>
    </div>
  );
}
